import React, { useState, useCallback } from "react";
import { usePageTitle } from "@/hooks/usePageTitle";
import { useCohortLedgerData } from "@/hooks/useGenericLedgerData";
import {
  PpcFarmCohortLedgerData,
  PpcFarmCohortDashboardSortOptions,
} from "@/types/ppcFarmTypes";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Users,
  ChevronDown,
  ChevronUp,
  X,
} from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import GenericLedgerDetailModal from "@/components/ppc-farm/GenericLedgerDetailModal";
import PpcFarmCohortMetricsBar from "./PpcFarmCohortMetricsBar";

// Helper function to humanize filter badge labels
const humanizeFilterLabel = (
  key: string,
  value: any,
): { label: string; displayValue?: string } => {
  const labelMap: Record<string, string> = {
    minOfProducts: "Min Products",
    maxOfProducts: "Max Products",
    minOfRatings: "Min Ratings",
    maxOfRatings: "Max Ratings",
    minOfOtherBrands: "Min Brands",
    maxOfOtherBrands: "Max Brands",
    minOfBrands: "Min Brands",
    maxOfBrands: "Max Brands",
    isUsSellers: "US Sellers",
    isUSSellers: "US Sellers",
    isUsProducts: "US Products",
    sunPosition: "Sun Position",
    totalCount: "Total Count",
    // Add more mappings as needed
  };

  // Special handling for isUsSellers to ensure it displays as "US Sellers"
  if (key === "isUsSellers" && value === true) {
    return { label: "US Sellers" };
  }

  const label =
    labelMap[key] ||
    key.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase());

  // For boolean true values, only show the label
  if (typeof value === "boolean" && value === true) {
    return { label };
  }

  // For other values, show Label: Value
  return {
    label,
    displayValue: typeof value === "number" ? value.toString() : value,
  };
};

// Enhanced filter badges component with proper overflow handling
const FilterBadges: React.FC<{
  filters: Record<string, any>;
  maxVisible?: number;
}> = ({ filters, maxVisible = 4 }) => {
  const badges = Object.entries(filters || {}).map(([key, value]) => {
    const { label, displayValue } = humanizeFilterLabel(key, value);
    return { key, label, displayValue };
  });

  const visibleBadges = badges.slice(0, maxVisible);
  const hiddenCount = badges.length - maxVisible;

  if (badges.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-1">
      {visibleBadges.map(({ key, label, displayValue }) => (
        <Badge key={key} variant="secondary" className="text-xs px-2 py-1">
          {displayValue ? `${label}: ${displayValue}` : label}
        </Badge>
      ))}

      {hiddenCount > 0 && (
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-xs hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              +{hiddenCount} more
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-2">
            <div className="flex flex-wrap gap-1">
              {badges.slice(maxVisible).map(({ key, label, displayValue }) => (
                <Badge key={key} variant="secondary" className="text-xs">
                  {displayValue ? `${label}: ${displayValue}` : label}
                </Badge>
              ))}
            </div>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
};

// Individual cohort performance card with improved layout
const CohortCard: React.FC<{
  cohort: PpcFarmCohortLedgerData;
  onClick: (cohort: PpcFarmCohortLedgerData) => void;
}> = ({ cohort, onClick }) => {
  const conversionRate = cohort.conversionRateSignedClients || 0;
  const signedClients = cohort.signedClients || 0;

  return (
    <Card
      className="min-h-[300px] cursor-pointer hover:shadow-lg transition-shadow duration-200 overflow-hidden"
      onClick={() => onClick(cohort)}
    >
      <CardContent className="p-5 h-full flex flex-col">
        {/* Header with Cohort Number and Badge */}
        <div className="flex items-start justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white pr-6">
            {cohort.cohortNumber}
          </h3>
          {cohort.isTriedAndTrue && (
            <Badge
              variant="default"
              className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 text-xs shrink-0"
            >
              Tried & True
            </Badge>
          )}
        </div>

        {/* Key Metrics - Better spacing */}
        <div className="space-y-3 mb-4">
          <div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
              Count:
            </p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">
              {cohort.count.toLocaleString()}
            </p>
          </div>

          <div className="flex justify-between items-center">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                Conversion Rate:
              </p>
              <p className="text-lg font-semibold text-gray-900 dark:text-white">
                {conversionRate.toFixed(2)}%
              </p>
            </div>
            {signedClients > 0 && (
              <div className="text-right">
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                  Signed:
                </p>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">
                  {signedClients}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Filter Badges - More space efficient */}
        <div className="flex-1 mb-4">
          <FilterBadges filters={cohort.filters_structured} maxVisible={3} />
        </div>

        {/* Footer with Creator and Date - Fixed at bottom */}
        <div className="text-xs text-gray-500 dark:text-gray-400 mt-auto border-t border-gray-100 dark:border-gray-700 pt-3">
          <p className="truncate mb-1">Created by: {cohort.yourName}</p>
          <p>
            {cohort.dateCreated.toDate().toLocaleDateString("en-US", {
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

// Control bar component
const CohortControlBar: React.FC<{
  filters: any;
  onFiltersChange: (filters: any) => void;
  sortOptions: PpcFarmCohortDashboardSortOptions;
  onSortChange: (sort: PpcFarmCohortDashboardSortOptions) => void;
  filterOptions: any;
  totalResults: number;
}> = ({
  filters,
  onFiltersChange,
  sortOptions,
  onSortChange,
  filterOptions,
  totalResults,
}) => {
  const handleSearchChange = (value: string) => {
    onFiltersChange({ ...filters, search: value });
  };

  const handleCreatorChange = (value: string) => {
    onFiltersChange({
      ...filters,
      yourName: value === "all" ? undefined : value,
    });
  };

  const handleStatusChange = (value: string) => {
    let newFilters = { ...filters };
    if (value === "all") {
      delete newFilters.isTriedAndTrue;
    } else if (value === "tried-and-true") {
      newFilters.isTriedAndTrue = "true";
    } else if (value === "experimental") {
      newFilters.isTriedAndTrue = "false";
    }
    onFiltersChange(newFilters);
  };

  const handleSortFieldChange = (field: string) => {
    onSortChange({
      field: field as PpcFarmCohortDashboardSortOptions["field"],
      direction: sortOptions.direction,
    });
  };

  const handleSortDirectionToggle = () => {
    onSortChange({
      field: sortOptions.field,
      direction: sortOptions.direction === "asc" ? "desc" : "asc",
    });
  };

  // Get current status filter value for display
  const getCurrentStatusValue = () => {
    if (!filters.isTriedAndTrue) return "all";
    return filters.isTriedAndTrue === "true"
      ? "tried-and-true"
      : "experimental";
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 mb-6">
      <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
        {/* Left-aligned Filtering Group */}
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
          {/* Creator Filter */}
          <Select
            value={filters.yourName || "all"}
            onValueChange={handleCreatorChange}
          >
            <SelectTrigger className="w-48">
              <SelectValue placeholder="All Creators" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Creators</SelectItem>
              {(filterOptions.yourNames || filterOptions.yourName || []).map(
                (creator: string) => (
                  <SelectItem key={creator} value={creator}>
                    {creator}
                  </SelectItem>
                ),
              )}
            </SelectContent>
          </Select>

          {/* Status Filter */}
          <Select
            value={getCurrentStatusValue()}
            onValueChange={handleStatusChange}
          >
            <SelectTrigger className="w-48">
              <SelectValue placeholder="All Types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="tried-and-true">Tried and True</SelectItem>
              <SelectItem value="experimental">Experimental</SelectItem>
            </SelectContent>
          </Select>

          {/* Clear Filters Button - Only show when filters are active */}
          {(filters.yourName || filters.isTriedAndTrue) && (
            <Button
              variant="outline"
              onClick={() =>
                onFiltersChange({
                  search: "",
                  yourName: "",
                  isTriedAndTrue: "",
                })
              }
              className="flex items-center gap-2"
            >
              <X className="h-4 w-4" />
              Clear Filters
            </Button>
          )}
        </div>

        {/* Right-aligned Sorting & Info Group */}
        <div className="flex items-center gap-4">
          <div className="text-sm text-gray-600 dark:text-gray-400">
            {totalResults.toLocaleString()} cohorts
          </div>

          <span className="text-sm text-gray-600 dark:text-gray-400">
            Sort by:
          </span>

          <div className="flex items-center gap-2">
            <Select
              value={sortOptions.field}
              onValueChange={handleSortFieldChange}
            >
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="cohortNumber">Cohort Number</SelectItem>
                <SelectItem value="count">Count</SelectItem>
                <SelectItem value="conversionRateSignedClients">
                  Conversion Rate
                </SelectItem>
                <SelectItem value="dateCreated">Date Created</SelectItem>
              </SelectContent>
            </Select>

            <Button
              variant="outline"
              size="sm"
              onClick={handleSortDirectionToggle}
              className="px-2"
            >
              {sortOptions.direction === "asc" ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

const PpcFarmCohortLedgerClient: React.FC = () => {
  usePageTitle("Cohort Ledger | PPC Farm Mail Tracker");

  // Modal state
  const [selectedItem, setSelectedItem] =
    useState<PpcFarmCohortLedgerData | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  // Use the custom hook for all data management - fetch all cohorts without pagination
  const {
    data,
    statistics,
    filterOptions,
    loading,
    error,
    pagination,
    filters,
    sortOptions,
    setFilters,
    setSortOptions,
  } = useCohortLedgerData({
    pageSize: 1000, // Large page size to get all cohorts at once
    initialSort: { field: "dateCreated", direction: "desc" },
  });

  // Handle sort changes with proper typing
  const handleSortChange = useCallback(
    (newSort: PpcFarmCohortDashboardSortOptions) => {
      setSortOptions(newSort);
    },
    [setSortOptions],
  );

  // Handle card click
  const handleCardClick = useCallback((item: PpcFarmCohortLedgerData) => {
    setSelectedItem(item);
    setIsDetailModalOpen(true);
  }, []);

  const handleCloseDetailModal = useCallback(() => {
    setIsDetailModalOpen(false);
    setSelectedItem(null);
  }, []);

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-2">
          Cohort Ledger
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Track and analyze cohort creation and performance
        </p>
      </div>

      {/* Cohort-Specific Metrics Bar */}
      <PpcFarmCohortMetricsBar
        data={data}
        loading={loading && data.length === 0}
        error={error}
      />

      {/* Control Bar */}
      <CohortControlBar
        filters={filters}
        onFiltersChange={setFilters}
        sortOptions={sortOptions as PpcFarmCohortDashboardSortOptions}
        onSortChange={handleSortChange}
        filterOptions={filterOptions}
        totalResults={pagination.totalItems}
      />

      {/* Error State */}
      {error && (
        <Card className="mb-6 border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20">
          <CardContent className="p-4">
            <p className="text-red-600 dark:text-red-400">
              Error loading cohorts: {error}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Cohort Cards Grid */}
      {loading && data.length === 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {[...Array(8)].map((_, i) => (
            <Card key={i} className="h-64 animate-pulse">
              <CardContent className="p-6">
                <div className="space-y-4">
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
                  <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded"></div>
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : data.length === 0 ? (
        <Card className="text-center py-12">
          <CardContent>
            <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              No cohorts found
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              No cohorts match your current filters. Try adjusting your search
              criteria.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {data.map((cohort) => (
            <CohortCard
              key={cohort.id}
              cohort={cohort}
              onClick={handleCardClick}
            />
          ))}
        </div>
      )}

      {/* Detail Modal */}
      <GenericLedgerDetailModal
        isOpen={isDetailModalOpen}
        onClose={handleCloseDetailModal}
        data={selectedItem}
        ledgerType="cohort"
      />
    </div>
  );
};

export default PpcFarmCohortLedgerClient;
